import type { SidebarProps } from '.';
import type { SidebarCategory } from '@readme/iso';

import React from 'react';

import useClassy from '@core/hooks/useClassy';
import { useSuperHubStore } from '@core/store';
import { isApiConfigCategory } from '@core/store/SuperHub/Sidebar/util';

import Group from './Group';
import SidebarList from './List';
import classes from './style.module.scss';

interface CategoriesProps {
  /**
   * Slug for the current page.
   */
  activeDoc?: string;
  activeItemRef: React.RefObject<HTMLAnchorElement>;
  alwaysExpanded: boolean;

  /**
   * Optional custom icon render function for the sidebar list items.
   */
  customIconRender: SidebarProps['customIconRender'];
  filteredCategories: { label: string | null; categories: SidebarCategory[] }[];

  /**
   * Sidebar link prefix.
   */
  pathRoot?: string;
}

const Categories = ({
  activeDoc,
  activeItemRef,
  alwaysExpanded,
  customIconRender,
  filteredCategories,
  pathRoot,
}: CategoriesProps) => {
  const bem = useClassy(classes, 'Sidebar');
  const [isSuperHub, routeSection] = useSuperHubStore(s => [s.isSuperHub, s.routeSection]);

  const renderCategory = (category: { label: string | null; categories: SidebarCategory[] }) => {
    const isPageGroup = category.label;

    const categoryList = category.categories.map(({ title, pages, slug, uri }) => {
      const hideCategoryTitle = isSuperHub && isApiConfigCategory(routeSection, title, pages);

      return (
        <section key={slug ?? uri} className={bem('-listWrapper', 'rm-Sidebar-section')}>
          {!hideCategoryTitle && <h2 className={bem('-heading', 'rm-Sidebar-heading')}>{title}</h2>}
          <SidebarList
            activeItemRef={activeItemRef}
            alwaysExpanded={alwaysExpanded}
            {...{ activeDoc, customIconRender, pathRoot, pages }}
          />
        </section>
      );
    });

    return isPageGroup ? (
      <Group key={category.label} label={category.label || ''}>
        {categoryList}
      </Group>
    ) : (
      categoryList
    );
  };

  return <>{Array.isArray(filteredCategories) && filteredCategories.map(renderCategory)}</>;
};

export default Categories;
